import React, { useEffect, useState } from "react";

import { faClose } from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  CheckForm,
  Flex,
  Group,
  Page,
  Search,
  Select,
  Spinner,
} from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";

import { QuoteStatus, ShipmentType } from "@assets/constants";
import { useAppDispatch, useAppSelector } from "@assets/hooks/redux";
import { ListQuoteVM, SelectOption } from "@assets/types";
import { ArrayUtilities, LabelMapperUtilities } from "@assets/utilities";
import { isAllActiveFilters } from "@assets/utilities/filterUtilities";
import { singleValueToSelectOptionMapper } from "@assets/utilities/labelMapperUtils";
import { QuoteCard } from "@components";
import {
  filterQuoteSearch,
  filterQuoteFromLocation,
  filterQuoteReset,
  filterQuoteShipmentType,
  filterQuoteStatus,
  filterQuoteToLocation,
  filterQuoteState,
  activeFilters,
} from "@state/slices/filterQuoteSlice";
import { getQuoteDeclineReasons } from "@state/slices/quoteReasons";
import { getQuotes, quotesState } from "@state/slices/quotesSlice";
import { userProfileState } from "@state/slices/userProfileSlice";

import StatusTags from "./List/statusTags";

const QuoteList = () => {
  const { t } = useTranslation();
  const userCompany =
    useAppSelector(userProfileState).userProfile?.SelectedCompany;

  const quotes = useAppSelector(quotesState).quotes;
  const isFetchingQuotes = useAppSelector(quotesState).isLoading;

  const filter = useAppSelector(filterQuoteState);
  const activeFilterList = useAppSelector(activeFilters);

  const dispatch = useAppDispatch();

  const [filteredQuotes, setFilteredQuotes] = useState<ListQuoteVM[]>([]);

  useEffect(() => {
    if (!isFetchingQuotes) {
      dispatch(getQuotes());
      dispatch(getQuoteDeclineReasons());
    }
  }, [userCompany]);

  useEffect(() => {
    setFilteredQuotes(getFilteredQuotes());
  }, [quotes, filter]);

  const handleSearchChange = (searchString: string) => {
    dispatch(filterQuoteSearch(searchString));
  };

  const handleStatusTagClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(filterQuoteStatus(e.currentTarget.value as QuoteStatus));
  };

  const handleResetFilters = () => {
    dispatch(filterQuoteReset());
  };

  const handleShipmentTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const toggledType = e.target.value;
    let updatedShipments: string[] = [...filter.shipmentTypes];
    if (e.target.checked) {
      updatedShipments = [...updatedShipments, toggledType];
    } else {
      updatedShipments.splice(filter.shipmentTypes.indexOf(toggledType), 1);
    }
    dispatch(filterQuoteShipmentType(updatedShipments));
  };

  const handleFromLocationChange = (
    selectedLocation: SelectOption<string> | null
  ) => {
    dispatch(
      filterQuoteFromLocation(selectedLocation ? selectedLocation.value : null)
    );
  };

  const handleToLocationChange = (
    selectedLocation: SelectOption<string> | null
  ) => {
    dispatch(
      filterQuoteToLocation(selectedLocation ? selectedLocation.value : null)
    );
  };

  const getFilteredQuotes = () =>
    quotes.filter((quote) => {
      return isAllActiveFilters(quote, filter);
    });

  return (
    <Page
      sidebar={{
        children: (
          <Flex gap="small" vertical>
            <Group title={t("LABEL_SHIPMENTTYPE")} vertical>
              <CheckForm
                checked={filter.shipmentTypes?.includes(ShipmentType.LCL)}
                id={ShipmentType.LCL}
                label={t("LABEL_LCLDEFINITION")}
                onChange={handleShipmentTypeChange}
                value={ShipmentType.LCL}
              />
              <CheckForm
                checked={filter.shipmentTypes?.includes(ShipmentType.FCL)}
                id={ShipmentType.FCL}
                label={t("LABEL_FCLDEFINITION")}
                onChange={handleShipmentTypeChange}
                value={ShipmentType.FCL}
              />
            </Group>
            <Select
              fullWidth
              isClearable
              label={t("LABEL_FROM")}
              onChange={handleFromLocationChange}
              options={LabelMapperUtilities.listToSelectOptionMapper(
                ArrayUtilities.unique(
                  quotes.map((quote) => quote.FromAddress.City).sort()
                )
              )}
              placeholder={t("LABEL_SELECTLOCATION")}
              value={singleValueToSelectOptionMapper(filter.fromLocation)}
            />
            <Select
              fullWidth
              isClearable
              label={t("LABEL_TO")}
              onChange={handleToLocationChange}
              options={LabelMapperUtilities.listToSelectOptionMapper(
                ArrayUtilities.unique(
                  quotes.map((quote) => quote.ToAddress.City).sort()
                )
              )}
              placeholder={t("LABEL_SELECTLOCATION")}
              value={singleValueToSelectOptionMapper(filter.toLocation)}
            />
          </Flex>
        ),
        headerAction: activeFilterList.length > 0 && (
          <Button
            iconLeft={faClose}
            onClick={handleResetFilters}
            variant="text"
          >
            {t("LABEL_CLEAR_FILTERS")}
          </Button>
        ),
        title: t("MENU_FILTER"),
      }}
      sidebarConfig={{ triggerLabel: t("MENU_FILTER"), alwaysOpen: "laptop" }}
      size="medium"
      title={t("MENU_QUOTES")}
    >
      <div className="flex flex-col gap-6">
        <Search
          className="w-[22.5em]"
          id="search-quote"
          onChange={handleSearchChange}
          placeholder={t("LABEL_SEARCH_BY_QUOTE_NUMBER")}
          value={filter.search}
        />
        <div className="flex flex-col">
          <div className="flex gap-2 overflow-x-auto pb-4 whitespace-nowrap">
            <StatusTags
              activeStatus={filter.status}
              onClick={handleStatusTagClick}
            />
          </div>
          {!isFetchingQuotes && filteredQuotes.length > 0 && (
            <p>{t("TEXT_N_QUOTES_FOUND", { count: filteredQuotes.length })}</p>
          )}
          {!isFetchingQuotes && filteredQuotes.length === 0 && (
            <p>{t("TEXT_NO_QUOTES_FOUND_ADJUST_FILTER")}</p>
          )}
        </div>
        {isFetchingQuotes ? (
          <Spinner fullWidth />
        ) : (
          filteredQuotes.map((quote: ListQuoteVM) => (
            <QuoteCard key={quote.QuoteReference} quote={quote} />
          ))
        )}
      </div>
    </Page>
  );
};

export default QuoteList;
